import React, { useEffect, useState, useCallback } from "react";
import { Coins, Save, Info } from "lucide-react";
import { Card, Button, Spinner, PageHeader, Select } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const FIELDS = [
  { key: "basic_salary", label: "Gaji Pokok (0 = pakai gaji karyawan)", group: "Pendapatan" },
  { key: "attendance_bonus", label: "Bonus Kehadiran Penuh", group: "Pendapatan" },
  { key: "kpi_bonus", label: "Bonus KPI", group: "Pendapatan" },
  { key: "kpi_bonus_threshold", label: "Ambang Skor KPI untuk Bonus", group: "Pendapatan" },
  { key: "meal_allowance_per_day", label: "Uang Makan / hari", group: "Tunjangan" },
  { key: "transport_allowance_per_day", label: "Uang Transport / hari", group: "Tunjangan" },
  { key: "overtime_rate_per_hour", label: "Tarif Lembur / jam", group: "Tunjangan" },
  { key: "late_penalty_per_min", label: "Denda Keterlambatan / menit", group: "Potongan" },
  { key: "absence_penalty_per_day", label: "Denda Absen / hari", group: "Potongan" },
  { key: "bpjs_pct", label: "BPJS % (siap di masa depan)", group: "Masa Depan" },
  { key: "pph21_pct", label: "PPh21 % (siap di masa depan)", group: "Masa Depan" },
];
const GROUPS = ["Pendapatan", "Tunjangan", "Potongan", "Masa Depan"];

export default function HRPayrollRules() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const [rules, setRules] = useState([]);
  const [pos, setPos] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/payroll-rules");
      setRules(data.rules);
      setPos((p) => p || data.rules[0]?.position);
    } catch { toast.error("Gagal memuat aturan payroll"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const r = rules.find((x) => x.position === pos);
    if (r) setDraft({ ...r });
  }, [pos, rules]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { position: pos };
      FIELDS.forEach(({ key }) => { payload[key] = Number(draft[key]) || 0; });
      await api.put("/hr/payroll-rules", payload);
      toast.success("Aturan payroll tersimpan"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="hr-payroll-rules-page">
      <PageHeader title="Aturan Payroll" subtitle="Komponen gaji per posisi (mendukung Pratinjau Payroll)" icon={Coins}>
        <Select value={pos} onChange={(e) => setPos(e.target.value)} data-testid="hr-payrule-position" className="!w-44">
          {rules.map((r) => <option key={r.position} value={r.position}>{r.position}</option>)}
        </Select>
      </PageHeader>

      {loading || !draft ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-xs text-blue-800" data-testid="hr-payrule-note">
            <Info size={14} className="mt-0.5 shrink-0" />
            Aturan ini mengisi <b>Pratinjau Payroll</b> (estimasi, bukan pembayaran). BPJS & PPh21 disiapkan untuk integrasi Finance/P&L di fase berikutnya.
          </div>
          {GROUPS.map((g) => (
            <Card key={g} data-testid={`hr-payrule-group-${g}`}>
              <h3 className="mb-3 font-heading font-bold text-ink">{g}</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {FIELDS.filter((f) => f.group === g).map((f) => (
                  <div key={f.key}>
                    <label className="cf-label">{f.label}</label>
                    <input type="number" className="cf-input" value={draft[f.key] ?? 0} disabled={!canManage}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} data-testid={`hr-payrule-${f.key}`} />
                  </div>
                ))}
              </div>
            </Card>
          ))}
          {canManage && <Button onClick={save} loading={saving} data-testid="hr-payrule-save"><Save size={15} /> Simpan Aturan</Button>}
        </div>
      )}
    </div>
  );
}
